import { useEffect, useState, type ReactNode } from 'react';
import { auth } from '@/config/firebase';
import { analyticsService } from '@/services/analytics/analyticsService';
import { applyTenantFont } from '@/services/tenant/tenantFont';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalizationStore } from '@/store/localizationStore';
import { activateLanguage } from '@/i18n';
import { getAppLanguage } from '@/i18n/appLanguage';
import {
  fetchWorkspaceId,
  fetchTenantTheme,
  fetchTenantContent,
  fetchTenantConfig,
  fetchTenantPreferences,
  fetchMobilePreferences,
  fetchHeaderFooterConfig,
} from '@/services/api/tenantApi';
import type { TenantTheme, TenantContent, TenantConfig } from '@/types/tenant.types';

/**
 * TenantProvider — mirrors Flutter's appInitializer() + _callApis().
 *
 * Lifecycle:
 *  1. Load cached data from localStorage (instant, non-blocking)
 *  2. Resolve workspaceId from hostname
 *  3. Set auth.tenantId = workspaceId (BEFORE any Firebase auth)
 *  4. Parallel-fetch: theme, content, config
 *  5. Apply CSS custom properties, favicon, title
 *  6. Check maintenance mode
 *  7. Resolve the app language (getAppLanguage) and load its strings — only now, so the
 *     request carries the WorkspaceId (waits only when there is no cached copy)
 *  8. Fetch preferences and header/footer config in the background
 *  9. Render children
 */

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantStore = useTenantStore();
  const localizationStore = useLocalizationStore();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // ── Phase 0: Load from cache ──
        loadCachedData();

        // ── Phase 1: Resolve workspaceId ──
        const cachedWorkspaceId = localStorage.getItem('workspaceId');
        let workspaceId: string;

        if (cachedWorkspaceId) {
          // Use cached immediately; re-fetch in background
          workspaceId = cachedWorkspaceId;
          tenantStore.setWorkspaceId(workspaceId);
          auth.tenantId = workspaceId;
          // Background refresh
          fetchWorkspaceId(window.location.hostname)
            .then((freshId) => {
              if (!cancelled && freshId && freshId !== workspaceId) {
                tenantStore.setWorkspaceId(freshId);
                auth.tenantId = freshId;
              }
            })
            .catch(() => {/* use cached */});
        } else {
          workspaceId = await fetchWorkspaceId(window.location.hostname);
          if (cancelled) return;
          tenantStore.setWorkspaceId(workspaceId);
          auth.tenantId = workspaceId;
        }

        // ── Phase 2: Parallel fetch theme + content + config ──
        const [theme, content, config] = await Promise.all([
          fetchTenantTheme(workspaceId).catch(() => getCachedTheme()),
          fetchTenantContent(workspaceId).catch(() => getCachedContent()),
          fetchTenantConfig().catch(() => getCachedConfig()),
        ]);
        if (cancelled) return;

        // Store the fetched data
        if (theme) tenantStore.setTheme(theme);
        if (content) tenantStore.setContent(content);
        if (config) {
          tenantStore.setConfig(config);
          if (config.supportedLanguage) {
            localizationStore.setSupportedLanguages(config.supportedLanguage);
          }
        }

        // ── Phase 3: Apply theme (CSS vars, font, favicon, title) ──
        if (theme) applyTheme(theme);
        if (content) applyContent(content);

        // ── Phase 4: Language strings ──
        // Flutter LocalizationProvider starts from getAppLanguage() once _callApis has loaded the
        // tenant config: the stored language, else the browser's if the tenant supports it, else 'en'
        const appLanguage = getAppLanguage();
        useLocalizationStore.setState({ currentLanguage: appLanguage });
        await activateLanguage(appLanguage);
        if (cancelled) return;

        // ── Phase 5: Preferences and configs (parallel, non-blocking) ──
        Promise.all([
          fetchTenantPreferences()
            .then((prefs) => { if (!cancelled) tenantStore.setPreferences(prefs); })
            .catch(() => {/* use cached */}),
          fetchMobilePreferences()
            .then((prefs) => { if (!cancelled) tenantStore.setMobilePreferences(prefs); })
            .catch(() => {/* use cached */}),
          fetchHeaderFooterConfig()
            .then((cfg) => { if (!cancelled) tenantStore.setHeaderFooterConfig(cfg); })
            .catch(() => {/* use cached */}),
        ]);

        tenantStore.setInitialized();

        // Initialize Firebase Analytics after tenant is ready
        analyticsService.init().catch(() => {/* analytics init is best-effort */});

        setIsReady(true);
      } catch (err) {
        console.error('[TenantProvider] Initialization failed:', err);
        setError('Failed to load application configuration.');
        // Still try to render with cached data. The strings request looks the workspace up
        // itself (apiClient, like getHeaders) and isn't cached if that fails too.
        const appLanguage = getAppLanguage();
        useLocalizationStore.setState({ currentLanguage: appLanguage });
        void activateLanguage(appLanguage);
        tenantStore.setInitialized();
        setIsReady(true);
      }
    }

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, system-ui, sans-serif',
        color: '#6B7280',
        fontSize: '14px',
      }}>
        Loading...
      </div>
    );
  }

  if (error && !tenantStore.isInitialized) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, system-ui, sans-serif',
        color: '#E57373',
        fontSize: '14px',
      }}>
        {error}
      </div>
    );
  }

  return <>{children}</>;
}

// ── Helpers ───────────────────────────────────────────────────────

function loadCachedData() {
  const tenantStore = useTenantStore.getState();
  const localizationStore = useLocalizationStore.getState();

  const cachedTheme = getCachedTheme();
  if (cachedTheme) {
    tenantStore.setTheme(cachedTheme);
    applyTheme(cachedTheme);
  }

  const cachedContent = getCachedContent();
  if (cachedContent) {
    tenantStore.setContent(cachedContent);
    applyContent(cachedContent);
  }

  const cachedConfig = getCachedConfig();
  if (cachedConfig?.supportedLanguage) {
    localizationStore.setSupportedLanguages(cachedConfig.supportedLanguage);
  }

  const cachedPrefs = tryParseJson<Record<string, unknown>>(localStorage.getItem('tenantPreferences'));
  if (cachedPrefs) {
    tenantStore.setPreferences(cachedPrefs);
  }

  // Flutter LocalDataService.getMobilePreferences() — read from the cache before the refresh
  const cachedMobilePrefs = tryParseJson<Record<string, unknown>>(localStorage.getItem('mobilePreferences'));
  if (cachedMobilePrefs) {
    tenantStore.setMobilePreferences(cachedMobilePrefs);
  }

  const cachedHfConfig = tryParseJson<Record<string, unknown>>(localStorage.getItem('headerFooterConfig'));
  if (cachedHfConfig) {
    tenantStore.setHeaderFooterConfig(cachedHfConfig);
  }
}

function getCachedTheme(): TenantTheme | null {
  return tryParseJson<TenantTheme>(localStorage.getItem('tenantTheme'));
}

function getCachedContent(): TenantContent | null {
  return tryParseJson<TenantContent>(localStorage.getItem('tenantContent'));
}

function getCachedConfig(): TenantConfig | null {
  return tryParseJson<TenantConfig>(localStorage.getItem('tenantConfig'));
}

function tryParseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/** Flutter ColorsConstants defaults */
const DEFAULT_PRIMARY_COLOR = '#083B94';
const DEFAULT_PRIMARY_COLOR_LIGHT = '#0056D6';
const DEFAULT_PRIMARY_COLOR_DARK = '#F7992C';
const DEFAULT_TOAST_BACKGROUND = '#A4A4A4';

/**
 * Flutter ColorsConstants' static colours. setTalentColors() falls back to the current value
 * when the theme has no (valid) colour, so a bad value never replaces a good cached one.
 */
const themeColors = {
  primaryColor: DEFAULT_PRIMARY_COLOR,
  primaryColorLight: DEFAULT_PRIMARY_COLOR_LIGHT,
  primaryColorDark: DEFAULT_PRIMARY_COLOR_DARK,
};

/**
 * Apply tenant theme as CSS custom properties on :root.
 * Mirrors Flutter ColorsConstants.setTalentColors() (+ ThemeData in app.dart) and WebFontConstants:
 *   --brand        primaryColor
 *   --brand-light  secondaryColor → Flutter primaryColorLight
 *   --accent       highlightColor → Flutter primaryColorDark (accent buttons)
 *   --toast-bg     '#' + highlightColor, else #A4A4A4 → Flutter toastBackgroundColor (web toasts)
 *   --footer-bg    footer.background.color, else primaryColor
 * --brand-dark (hover / pressed) and --brand-soft (tints) are derived from the primary colour.
 */
function applyTheme(theme: TenantTheme): void {
  const root = document.documentElement;
  const ts = theme.themeSettings;

  themeColors.primaryColor = hexToColor(ts?.primaryColor) ?? themeColors.primaryColor;
  themeColors.primaryColorLight = hexToColor(ts?.secondaryColor) ?? themeColors.primaryColorLight;
  themeColors.primaryColorDark = hexToColor(ts?.highlightColor) ?? themeColors.primaryColorDark;
  const primary = themeColors.primaryColor;

  root.style.setProperty('--brand', primary);
  root.style.setProperty('--brand-dark', darkenHex(primary, 20));
  root.style.setProperty('--brand-soft', lightenHex(primary, 85));
  root.style.setProperty('--brand-light', themeColors.primaryColorLight);
  root.style.setProperty('--accent', themeColors.primaryColorDark);
  root.style.setProperty('--toast-bg', toastBackgroundColor(ts?.highlightColor));
  root.style.setProperty('--footer-bg', hexToColor(ts?.footer?.background?.color) ?? primary);

  // Tenant font — mirrors WebFontConstants.getFontFamily()
  if (ts) void applyTenantFont(ts.primaryFontFamilyName);
}

/**
 * Apply tenant content: favicon + document title.
 * Mirrors Flutter GenericHelpers.updateTenantFaviconAndTitle() + setTenantStaticContentData().
 */
function applyContent(content: TenantContent): void {
  const general = content.contentSettings?.general;
  if (!general) return;

  // Favicon
  if (general.faviconImageUrl) {
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (link) {
      link.href = general.faviconImageUrl;
    }
  }

  // Title — Flutter MaterialApp(title: contentSettings.general.name ?? 'Career Portal')
  if (general.name) {
    document.title = general.name;
  }
}

// ── Simple color utils ───────────────────────────────────────────

/**
 * Flutter ColorsConstants.hexToColor(): upper-case, drop every '#', and accept exactly six hex
 * digits (opaque RGB); anything else is null so the caller falls back.
 */
function hexToColor(value: string | null | undefined): string | null {
  if (value == null) return null;
  const hex = value.toUpperCase().replace(/#/g, '');
  return /^[0-9A-F]{6}$/.test(hex) ? `#${hex}` : null;
}

/**
 * Flutter toastBackgroundColor = fixHexColor(highlightColor ?? 'A4A4A4') — the raw value with a
 * leading '#'. Flutter hands that string to the browser as-is; a value CSS can't read would leave
 * the toast without a background, so it falls back to the default grey instead.
 */
function toastBackgroundColor(highlightColor: string | null | undefined): string {
  const raw = highlightColor ?? DEFAULT_TOAST_BACKGROUND.slice(1);
  const fixed = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(fixed) ? fixed : DEFAULT_TOAST_BACKGROUND;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function darkenHex(hex: string, percent: number): string {
  const [r, g, b] = hexToRgb(hex);
  const factor = 1 - percent / 100;
  return rgbToHex(r * factor, g * factor, b * factor);
}

function lightenHex(hex: string, percent: number): string {
  const [r, g, b] = hexToRgb(hex);
  const factor = percent / 100;
  return rgbToHex(r + (255 - r) * factor, g + (255 - g) * factor, b + (255 - b) * factor);
}
